import { type PriceScaleMode } from '@/features/chart/lib/utils/chart-util'
import {
  IncrementalTicker,
  InstrumentKind,
  InstrumentType,
  TickSizeStep,
} from '@/types/generated/common/types'

export type ChartAnalyticsMetric =
  | 'vwap'
  | 'twap'
  | 'liqwap'
  | 'volumeBase'
  | 'volumeQuote'
  | 'buyVolumeBase'
  | 'sellVolumeBase'
  | 'netBuyVolumeBase'
  | 'cumulativeVolumeDelta'
  | 'netBuyVolumeQuote'
  | 'deltaPercentage'
  | 'totalLiqBase'
  | 'totalLiqQuote'
  | 'buySellLiq'
  | 'liqTakerVolume'
  | 'liqMakerVolume'
  | 'totalLiqCount'
  | 'netLiquidations'
  | 'totalTradeCount'
  | 'buyTradeCount'
  | 'sellTradeCount'
  | 'netBuyTradeCount'
  | 'avgTradeSize'
  | 'avgBuyTradeSize'
  | 'avgSellTradeSize'
  | 'parkinsonVolatility'
  | 'vwapTwapSpread'
  | 'volumeBaseBlockIncl'
  | 'volumeQuoteBlockIncl'
  | 'buyVolumeBaseBlockIncl'
  | 'sellVolumeBaseBlockIncl'
  | 'netBuyVolumeBaseBlockIncl'
  | 'netBuyVolumeQuoteBlockIncl'
  | 'openInterestBase'
  | 'openInterestQuote'
  | 'aggregatedOpenInterest'
  | 'fundingRate'
  | 'aggregatedFundingRateAvgClose'
  | 'fundingApr'
  | 'putCallRatioOi'
  | 'putCallRatioVolume'
  | 'optionsSkew25d'
  | 'optionsVolume'
  | 'obSpreadBps'
  | 'obPressureRatio'
  | 'obBidLiquidityTotal'
  | 'obAskLiquidityTotal'
  | 'obDepthImbalance'
  | 'crossExchangeOpenInterest'
  | 'crossExchangeFundingRate'
  | 'crossExchangeCvd'

export type ChartAnalyticsPlacement = 'overlay' | 'pane'

export type ChartAnalyticsCategory =
  | 'price'
  | 'volume'
  | 'liquidations'
  | 'microstructure'
  | 'volatility'
  | 'derivatives'
  | 'cross-exchange'

export type ChartAnalyticsUnit = 'base' | 'quote'

export type VisibleRangeProfileId =
  | 'volumeDeltaProfile'
  | 'liquidationProfile'
  | 'openInterestProfile'

export type VisibleRangeProfileUnit = 'base' | 'quote'

// Hyperliquid liquidation & stop-level profiles (oems #261). Kept separate from
// VisibleRangeProfileId — a structurally different data pipeline (live AnalyticsStream, not a
// one-shot visible-range HTTP request), even though the two families share rendering
// conventions.
export type LevelProfileId = 'liquidationLevelProfile' | 'stopLevelProfile'

export type LevelProfileMeasure = 'notional' | 'count'

export type ChartAnalyticsSuite =
  | 'price-overlays'
  | 'advanced-volume-profile'
  | 'order-flow-delta'
  | 'liquidation-map'
  | 'market-microstructure'
  | 'volatility-spreads'
  | 'derivatives'
  | 'cross-exchange'

export type ChartAnalyticsSeriesOption = {
  enabled: boolean
  placement: ChartAnalyticsPlacement
  suite?: ChartAnalyticsSuite
  category?: ChartAnalyticsCategory
}

export type ChartAnalyticsOption = {
  vwap: ChartAnalyticsSeriesOption
  twap: ChartAnalyticsSeriesOption
  liqwap: ChartAnalyticsSeriesOption
  volumeBase: ChartAnalyticsSeriesOption
  volumeQuote: ChartAnalyticsSeriesOption
  buyVolumeBase: ChartAnalyticsSeriesOption
  sellVolumeBase: ChartAnalyticsSeriesOption
  netBuyVolumeBase: ChartAnalyticsSeriesOption
  cumulativeVolumeDelta: ChartAnalyticsSeriesOption
  netBuyVolumeQuote: ChartAnalyticsSeriesOption
  deltaPercentage: ChartAnalyticsSeriesOption
  totalLiqBase: ChartAnalyticsSeriesOption
  totalLiqQuote: ChartAnalyticsSeriesOption
  buySellLiq: ChartAnalyticsSeriesOption
  liqTakerVolume: ChartAnalyticsSeriesOption
  liqMakerVolume: ChartAnalyticsSeriesOption
  totalLiqCount: ChartAnalyticsSeriesOption
  netLiquidations: ChartAnalyticsSeriesOption
  totalTradeCount: ChartAnalyticsSeriesOption
  buyTradeCount: ChartAnalyticsSeriesOption
  sellTradeCount: ChartAnalyticsSeriesOption
  netBuyTradeCount: ChartAnalyticsSeriesOption
  avgTradeSize: ChartAnalyticsSeriesOption
  avgBuyTradeSize: ChartAnalyticsSeriesOption
  avgSellTradeSize: ChartAnalyticsSeriesOption
  parkinsonVolatility: ChartAnalyticsSeriesOption
  vwapTwapSpread: ChartAnalyticsSeriesOption
  volumeBaseBlockIncl: ChartAnalyticsSeriesOption
  volumeQuoteBlockIncl: ChartAnalyticsSeriesOption
  buyVolumeBaseBlockIncl: ChartAnalyticsSeriesOption
  sellVolumeBaseBlockIncl: ChartAnalyticsSeriesOption
  netBuyVolumeBaseBlockIncl: ChartAnalyticsSeriesOption
  netBuyVolumeQuoteBlockIncl: ChartAnalyticsSeriesOption
  openInterestBase: ChartAnalyticsSeriesOption
  openInterestQuote: ChartAnalyticsSeriesOption
  aggregatedOpenInterest: ChartAnalyticsSeriesOption
  fundingRate: ChartAnalyticsSeriesOption
  aggregatedFundingRateAvgClose: ChartAnalyticsSeriesOption
  fundingApr: ChartAnalyticsSeriesOption
  putCallRatioOi: ChartAnalyticsSeriesOption
  putCallRatioVolume: ChartAnalyticsSeriesOption
  optionsSkew25d: ChartAnalyticsSeriesOption
  optionsVolume: ChartAnalyticsSeriesOption
  obSpreadBps: ChartAnalyticsSeriesOption
  obPressureRatio: ChartAnalyticsSeriesOption
  obBidLiquidityTotal: ChartAnalyticsSeriesOption
  obAskLiquidityTotal: ChartAnalyticsSeriesOption
  obDepthImbalance: ChartAnalyticsSeriesOption
  crossExchangeOpenInterest: ChartAnalyticsSeriesOption
  crossExchangeFundingRate: ChartAnalyticsSeriesOption
  crossExchangeCvd: ChartAnalyticsSeriesOption
}

/**
 * Percept's Main Chart display options, in the shape of its own settings dialog (converted to
 * main-chart settings with `dialogSettingsToMainChartSettings`). Unset keys keep Percept's default.
 */
export type PerceptChartSettings = {
  precisionMode?: 'default' | 'integer' | 'manual'
  /** Used when `precisionMode` is `manual`. */
  decimalPlaces?: number
  candleCountdown?: boolean
  momentumArrows?: boolean
  showPriceLine?: boolean
  maximaMode?: 'off' | 'basic' | 'advanced'
  showOffscreenLivePill?: boolean
}

// Chart configuration
export type ChartOption = {
  timeframe: number
  scale: keyof typeof PriceScaleMode
  chartType?: number
  perceptChartType?: string
  content?: string
  /** Percept's saved data (studies, drawings, layout); TV's lives in `content`. */
  perceptContent?: string
  analytics?: ChartAnalyticsOption
  showPositionLine?: boolean
  showLiquidationLine?: boolean
  showBuySellTriangles?: boolean
  showBuySellButtons?: boolean
  showOpenOrders?: boolean
  showIndex?: boolean
  showTopBar?: boolean
  /** Percept only: the viewport high/low pills on the price scale. */
  showHighLow?: boolean
  /** Percept only: its Main Chart display options, set from our chart settings menu. */
  perceptChartSettings?: PerceptChartSettings
  autoScale?: boolean
  volumeDeltaProfileEnabled?: boolean
  liquidationProfileEnabled?: boolean
  openInterestProfileEnabled?: boolean
  volumeDeltaProfileUnit?: VisibleRangeProfileUnit
  liquidationProfileUnit?: VisibleRangeProfileUnit
  openInterestProfileUnit?: VisibleRangeProfileUnit
  /** Legacy shared unit kept for restoring older saved layouts. */
  visibleRangeProfileUnit?: VisibleRangeProfileUnit
  liquidationLevelProfileEnabled?: boolean
  stopLevelProfileEnabled?: boolean
  liquidationLevelProfileMeasure?: LevelProfileMeasure
  stopLevelProfileMeasure?: LevelProfileMeasure
  // Per-bucket "S 6.91M / L 0"-style text overlay toggle — off by default request reads as
  // clutter once several buckets are non-zero at once, so each profile lane gets its own
  // on/off switch via its TV study settings. Default true (shown), matching prior behavior.
  volumeDeltaProfileShowLabels?: boolean
  liquidationProfileShowLabels?: boolean
  openInterestProfileShowLabels?: boolean
  liquidationLevelProfileShowLabels?: boolean
  stopLevelProfileShowLabels?: boolean
  orderbookHeatmapEnabled?: boolean
  /** ± % of mid the order-book heatmap draws (both chart engines). */
  orderbookHeatmapRange?: number
  /** Order-book heatmap price grouping, as a multiple of the server's bucket (1 = off). */
  orderbookHeatmapGrouping?: number
  /** Order-book heatmap size filter: only levels resting between these are drawn (0 = no bound). */
  orderbookHeatmapMinSize?: number
  orderbookHeatmapMaxSize?: number
}

// Position table tabs
export enum PositionTableTab {
  POSITIONS = 'POSITIONS',
  ACTIVE = 'ACTIVE',
  HISTORY = 'HISTORY',
  UNIFIED_HISTORY = 'UNIFIED_HISTORY',
}

// The six sections the UNIFIED_HISTORY panel shows as an accordion — also the
// six extra top-level tabs the Positions widget's tab row shows when the
// History tab is "expanded" (see positions-tab-contents.tsx /
// unified-history-table.tsx). Deliberately NOT added to PositionTableTab
// itself: several consumers (service/ai.hook.ts's toActiveTab, in
// particular) switch exhaustively over that enum with a `default:` branch,
// so widening it would let new tabs silently fall through instead of
// producing a type error.
export type HistorySectionKey = 'orders' | 'hedges' | 'triggers' | 'rfq' | 'arb' | 'spreader'

export const HISTORY_SECTION_KEYS: readonly HistorySectionKey[] = [
  'orders',
  'hedges',
  'triggers',
  'rfq',
  'arb',
  'spreader',
]

// Maps each section to the i18n key its label uses in the `trade` namespace —
// shared by the accordion's GroupedSectionHeader and the expanded tab row so
// both modes read identically.
export const HISTORY_SECTION_I18N_KEY: Record<HistorySectionKey, string> = {
  orders: 'orders',
  hedges: 'hedges',
  triggers: 'triggers',
  rfq: 'rfqs',
  arb: 'arb',
  spreader: 'spreader',
}

const HISTORY_SECTION_TAB_PREFIX = 'HISTORY_SECTION:' as const
export type HistorySectionTab = `${typeof HISTORY_SECTION_TAB_PREFIX}${HistorySectionKey}`

/** Everything the Positions widget's tab row / tab atom can hold. */
export type PositionsTabValue = PositionTableTab | HistorySectionTab

export function historySectionTab(key: HistorySectionKey): HistorySectionTab {
  return `${HISTORY_SECTION_TAB_PREFIX}${key}`
}

/** Returns the section key if `value` is a section tab, else null (also rejects unknown/stale keys). */
export function historySectionOf(value: PositionsTabValue): HistorySectionKey | null {
  if (!value.startsWith(HISTORY_SECTION_TAB_PREFIX)) return null
  const key = value.slice(HISTORY_SECTION_TAB_PREFIX.length) as HistorySectionKey
  return HISTORY_SECTION_KEYS.includes(key) ? key : null
}

/** True for UNIFIED_HISTORY itself or any of its section tabs — used by header-action gating. */
export function isHistoryLikeTab(value: PositionsTabValue): boolean {
  return value === PositionTableTab.UNIFIED_HISTORY || historySectionOf(value) !== null
}

/**
 * A section tab is only meaningful while History is expanded — collapsing (or
 * restoring/duplicating a value produced while expanded, into a context that
 * isn't) must fold it back into UNIFIED_HISTORY, or Radix Tabs would render
 * no matching panel at all.
 */
export function normalizePositionsTab(
  value: PositionsTabValue,
  expanded: boolean
): PositionsTabValue {
  if (!expanded && historySectionOf(value)) return PositionTableTab.UNIFIED_HISTORY
  return value
}

/**
 * Whether a selection row's `accountId` may be retargeted by the order form's AccountTab.
 * `scoped` rows are bound to an existing target (a position, an open order, an
 * account-context-menu action) and must never be retargeted — the resolver validates
 * only the bound account and blocks the row rather than falling back. `inherited` rows
 * have no bound account of their own and follow the form's account resolution (form
 * override → scoped peer → remembered → card → venue default).
 *
 * Required (not optional) so a new producer that forgets to set it fails typecheck
 * instead of silently defaulting — see CLAUDE.md-adjacent note in
 * lib/utils/resolve-account.ts. Producers declare this explicitly; transformers/restorers
 * that copy an existing row must preserve it, never assign it.
 */
export type AccountBinding =
  | {
      kind: 'scoped'
      // 'position': derived straight from a Position's own accountId (close, roll,
      // shift-click). 'existing-order': carried from any other already-account-bound
      // source outside the form's own resolution — an open/historical order, a spreader
      // leg or trigger-dispatch order being duplicated. 'account-context-menu': set by an
      // explicit right-click/menu account action. 'origin-selection': the account shown in
      // the originating widget's own account selector (e.g. the chart's) — a row is added on
      // the account the user is looking at there, not the form's per-venue choice.
      // 'user-selection': the user moved this row onto an account in the Order Form itself
      // (the row's "move to account" menu).
      reason:
        | 'position'
        | 'existing-order'
        | 'account-context-menu'
        | 'origin-selection'
        | 'user-selection'
    }
  | { kind: 'inherited' }

/** The binding for a row with no bound source account — it follows the form's own
 * account resolution (form override → scoped peer → remembered → card → venue default). */
export const INHERITED_ACCOUNT_BINDING: AccountBinding = { kind: 'inherited' }

// Selected options from option chain (multi-select)
export type SelectedOption = {
  accountId?: string
  accountBinding: AccountBinding
  exchange?: string
  instrumentName: string
  side: 'ask' | 'bid'
  tradeSide: 'buy' | 'sell'
  price: string
  amount?: string
  // See SelectedInstrument.lockedAmount.
  lockedAmount?: boolean
  ticker?: IncrementalTicker
  minTradeAmount?: string
  strikePrice?: string
  optionType?: 'call' | 'put'
  // Set by activateSpreader (Pro dropdown "Spreader" pick) or handleSpreaderRoleChange (badge
  // toggle) — unlike SelectedInstrument.spreaderRole, never by the spread-matrix widget, which
  // only ever adds perp/future legs. Presence of a 'quoting'-tagged leg anywhere in the form's
  // selection (instrument OR option) is what puts the unified order form into spreader mode —
  // see isSpreaderMode in use-unified-order-form.ts.
  spreaderRole?: 'quoting' | 'taking'
}

// Selected instruments from watchlist (non-options: Spot, Perp, Future)
export type SelectedInstrument = {
  accountId?: string
  accountBinding: AccountBinding
  symbol: string
  baseCurrency: string
  quoteCurrency: string
  instrumentKind: string
  side: 'buy' | 'sell'
  lastPrice?: string
  exchange?: string
  exchangeSymbol?: string
  exchangeId?: string
  internalId?: string
  settlementCurrency?: string
  tickSize?: string
  minTradeAmount?: string
  contractSize?: string
  pricePrecision?: string
  qtyPrecision?: string
  expirationTsMs?: string
  fundingInterval?: string
  isAutoFromChart?: boolean // True if auto-registered from current chart ticker
  amount?: string // Pre-filled quantity (e.g. from position size)
  type?: InstrumentType // Linear/inverse from proto metadata (preferred over symbol heuristic)
  // Set by a spread-matrix widget cell click (row = 'quoting', column = 'taking'). Presence of
  // a 'quoting'-tagged row anywhere in the form's selection is what puts the unified order
  // form into spreader mode — see isSpreaderMode in use-unified-order-form.ts.
  spreaderRole?: 'quoting' | 'taking'
  // Set by "Close position" when the venue closes the full position only (no partial close):
  // the size box renders read-only at the position's size.
  lockedAmount?: boolean
  // Native position quantity unit. Used only while lockedAmount is true.
  lockedAmountUnit?: string
}

export type DashboardColumn = {
  instrument: boolean
  type: boolean
  size: boolean
  delta: boolean
  deltaUSD: boolean
  gamma: boolean
  gammaUSD: boolean
  vega: boolean
  vegaUSD: boolean
  theta: boolean
  thetaUSD: boolean
  rho: boolean
  rhoUSD: boolean
  entry: boolean
  entryIv: boolean
  mark: boolean
  markIv: boolean
  pnl: boolean
  pnlUSD: boolean
  pnlPercent: boolean
  netPnl: boolean
  realizedPnl: boolean
  realizedPnlUSD: boolean
  im: boolean
  imUSD: boolean
  mm: boolean
  mmUSD: boolean
  liquidationPrice: boolean
  dailyUnrealizedPnl: boolean
  dailyUnrealizedPnlUsd: boolean
  realizedFunding: boolean
  expiry: boolean
  account: boolean
}

export type PositionFilter = {
  instruments: { disabled: boolean; value: string[] }
  expiries: { disabled: boolean; value: string[] }
  assets: { disabled: boolean; value: string[] }
  /** Filters by accountId — 'ALL' means every active account. */
  accounts: { disabled: boolean; value: string[] }
  size: { disabled: boolean; value: { from: number; to: number } | null }
  pnl: { disabled: boolean; value: { from: number; to: number } | null }
  strikeRange: { disabled: boolean; value: { from: number; to: number } | null }
  positionDelta: { disabled: boolean; value: { from: number; to: number } | null }
}

export type DashboardConifg = {
  columns: DashboardColumn
  columnsOrder: number[]
  positionFilter: PositionFilter
}

export type WatchListItem = {
  symbol: string
  baseCurrency: string
  quoteCurrency: string
  instrumentKind: InstrumentKind
  exchange: number
  exchangeSymbol?: string
  exchangeId?: string
  internalId?: string
  settlementCurrency?: string
  tickSize?: string
  tickSizeSteps?: TickSizeStep[]
  minTradeAmount?: string
  contractSize?: string
  pricePrecision?: string
  qtyPrecision?: string
  expirationTimestamp?: string // For sorting futures
  expirationTsMs?: string
  fundingInterval?: string
  closedPrice?: string
  lastPrice?: string
  prevLastPrice?: string // Previous price for change highlighting
  bestBidPrice?: string
  prevBestBidPrice?: string
  bestAskPrice?: string
  prevBestAskPrice?: string
  volume24h?: string
  priceChange24h?: string
  priceChangePercent24h?: string
  high24h?: string
  low24h?: string
}
